import { Lightbulb } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useOptionalAgentChat } from '@/contexts/AgentChatContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { isNonProductionDebugEnvironment } from '@/lib/debugClipboard';
import { getConfiguredFrontChatId } from '@/lib/frontChat';
import { useManagedFloatingWidgetPlacement, type ManagedFloatingWidgetId } from '@/lib/floatingWidgetLayout';
import { MANAGED_FLOATING_WIDGET_ATTRIBUTE } from '@/lib/floatingWidgetSuppression';
import { ADMIN_ACCENT_SWITCH_CLASSES } from '@/lib/surfaceTonePalette';
import { cn } from '@/lib/utils';
import { useWidgetPreferences } from '@/lib/widgetPreferences';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useSuperAdminStatus } from '@/hooks/useIsSuperAdmin';
import { FEATURE_FLAG_KEYS } from '@/lib/featureFlagsCatalog';
import {
  setSuperAdminHintsEnabled,
  useSuperAdminHintsPreference,
} from '@/lib/superAdminHintsPreference';

/**
 * ENT-5840: the super-admin Hints switch floats in the bottom-right control stack beside the
 * debug and support-chat buttons, so it stays visible (and clickable) over the Atlas dialog.
 * ENT-6060: it wears the admin Switch Profile accent (`ADMIN_ACCENT_SWITCH_CLASSES`): an outline
 * when off and the solid fill the Admin Profile link takes on hover when on.
 */
export function SuperAdminHintsButton({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  const { preferences } = useWidgetPreferences();
  const agentChat = useOptionalAgentChat();
  const agentChatMinimized = Boolean(agentChat?.isOpen && agentChat.isMinimized);
  // Reserving a slot that is empty only widens the gap; missing an occupied one overlaps it.
  const companionWidgetIds: ManagedFloatingWidgetId[] = [
    ...(preferences.front && getConfiguredFrontChatId() ? (['front-chat'] as const) : []),
    'agent-fab',
    ...(isNonProductionDebugEnvironment() ? (['debug-clipboard'] as const) : []),
    ...(agentChatMinimized ? (['agent-chat-minimized'] as const) : []),
  ];
  const placement = useManagedFloatingWidgetPlacement('super-admin-hints', [...companionWidgetIds, 'super-admin-hints']);
  // Always visible and clickable (Jono, 2026-10-21): no scroll/overlap suppression, unlike the
  // other floating widgets, because its job is to work over the Atlas dialog's own controls.
  // The one exception: the full SupplySteward panel owns this corner while it is open, so the
  // switch steps aside (as the Sentry trigger does) and returns when the panel is minimized or closed.
  if (agentChat?.isOpen && !agentChat.isMinimized) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Show or hide super admin hints"
          data-hints={enabled ? 'on' : 'off'}
          data-state={enabled ? 'on' : 'off'}
          size="icon"
          variant="ghost"
          className={cn(
            'pointer-events-auto fixed z-[2147483647] h-10 w-10 rounded-full border-[1.5px] shadow-lg transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-rose-500',
            enabled ? ADMIN_ACCENT_SWITCH_CLASSES.on : ADMIN_ACCENT_SWITCH_CLASSES.off,
          )}
          style={{ right: `${placement.right}px`, bottom: `${placement.bottom}px` }}
          {...{ [MANAGED_FLOATING_WIDGET_ATTRIBUTE]: 'super-admin-hints' }}
          onClick={onToggle}
        >
          <Lightbulb className="h-5 w-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">Show or hide super admin hints</TooltipContent>
    </Tooltip>
  );
}

/** Shared shell gate for the Hints control. The preference is global, while visibility is always
 * resolved from the authenticated user, the feature flag, and the verified super-admin lookup. */
export function SuperAdminHintsControl() {
  const { user } = useOrganization();
  const superAdminStatus = useSuperAdminStatus(user?.id);
  const atlasFindingsFlag = useFeatureFlag(FEATURE_FLAG_KEYS.atlasFindingsView, { userId: user?.id });
  const enabled = useSuperAdminHintsPreference();
  const lookupPending = superAdminStatus.isLoading || Boolean(superAdminStatus.error);

  if (!superAdminStatus.isSuperAdmin || lookupPending || !atlasFindingsFlag.enabled) {
    return null;
  }

  return (
    <SuperAdminHintsButton
      enabled={enabled}
      onToggle={() => setSuperAdminHintsEnabled(!enabled)}
    />
  );
}
